import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner,
} from "react-bootstrap";
import {
  FaFileExcel, FaChartLine, FaMoneyBillWave, FaBalanceScale,
  FaHandshake, FaCogs, FaPercent,
} from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import ReportSummaryCard from "./components/ReportSummaryCard";
import PeriodCompareLineChart from "./components/PeriodCompareLineChart";
import { PERIOD_OPTIONS, buildPeriodParams, getPeriodLabel } from "./utils/periodFilter";
import { buildChips, countActiveChips, formatDateChip, resolveOptionLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import { formatVND as fmtVND, formatMoney } from "./utils/moneyFormat";
import "./accountingFilters.css";
import "./accountingResponsive.css";

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_PERIOD = "MONTH";
const isCanceled = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";

const buildDefaultFilters = () => ({
  lineId: "",
  period: DEFAULT_PERIOD,
  ...buildPeriodParams(DEFAULT_PERIOD),
});
const fmtShort = (v) => {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)} tỷ`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)} tr`;
  return formatMoney(n);
};

const profitColor = (v) => (Number(v || 0) < 0 ? "#dc3545" : "#0d6efd");

const ProfitList = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [chartMetric, setChartMetric] = useState("gross"); // gross | net | byLine

  const [lines, setLines] = useState([]);
  const initial = useMemo(() => buildDefaultFilters(), []);
  const [filterLine, setFilterLine] = useState("");
  const [filterFrom, setFilterFrom] = useState(initial.dateFrom || "");
  const [filterTo, setFilterTo] = useState(initial.dateTo || "");
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [appliedFilters, setAppliedFilters] = useState(initial);
  const [report, setReport] = useState(null);

  const fetchSeqRef = useRef(0);
  const abortRef = useRef(null);
  const filtersRef = useRef(appliedFilters);
  filtersRef.current = appliedFilters;

  const chipDefs = useMemo(() => [
    {
      key: "period",
      label: "Thời gian",
      isActive: (v) => v && v !== DEFAULT_PERIOD,
      formatValue: (v, applied) => (
        v === "CUSTOM"
          ? formatDateChip(applied.dateFrom, applied.dateTo)
          : PERIOD_OPTIONS.find((o) => o.value === v)?.label || v
      ),
    },
    {
      key: "lineId",
      label: "Line KD",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveOptionLabel(v, lines),
    },
  ], [lines]);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const fetchDropdowns = useCallback(async () => {
    try {
      const lRes = await axiosInstance.get("/api/accounting/revenues/lines").catch(() => null);
      const lineData = unwrap(lRes);
      if (lineData) setLines(Array.isArray(lineData) ? lineData : []);
    } catch (_) { /* silent */ }
  }, []);

  const fetchData = useCallback(async (filters) => {
    const next = filters || filtersRef.current;
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = { period: next.period || DEFAULT_PERIOD };
      if (next.lineId) params.lineId = next.lineId;
      if (next.dateFrom) params.dateFrom = next.dateFrom;
      if (next.dateTo) params.dateTo = next.dateTo;
      const res = await axiosInstance.get("/api/accounting/profit/report", {
        params,
        signal: controller.signal,
      });
      if (seq !== fetchSeqRef.current) return;
      setReport(unwrap(res));
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải báo cáo lợi nhuận");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
    const defaults = buildDefaultFilters();
    setAppliedFilters(defaults);
    setPeriod(defaults.period);
    setFilterFrom(defaults.dateFrom || "");
    setFilterTo(defaults.dateTo || "");
    fetchData(defaults);
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    filtersRef.current = next;
    setReport(null);
    fetchData(next);
  };

  const syncDraft = (filters) => {
    setFilterLine(filters.lineId || "");
    setPeriod(filters.period || DEFAULT_PERIOD);
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const buildNextFilters = () => {
    const range = period === "CUSTOM"
      ? { dateFrom: filterFrom, dateTo: filterTo }
      : buildPeriodParams(period);
    return {
      lineId: filterLine,
      period,
      dateFrom: range.dateFrom || "",
      dateTo: range.dateTo || "",
    };
  };

  const handleSearch = () => applyFilters(buildNextFilters());
  const handleReset = () => {
    const defaults = buildDefaultFilters();
    syncDraft(defaults);
    applyFilters(defaults);
  };
  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "period") {
      const defaults = buildDefaultFilters();
      Object.assign(base, defaults);
      base.lineId = appliedFilters.lineId;
    } else {
      base[key] = "";
    }
    syncDraft(base);
    applyFilters(base);
  };

  const exportExcel = () => {
    if (!report) { toast.warn("Không có dữ liệu để xuất"); return; }
    const byLineRows = report.byLine || [];
    const wb = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet([
      { Chỉ_số: "Doanh thu", Giá_trị: report.totalRevenue || 0 },
      { Chỉ_số: "Chi phí NCC", Giá_trị: report.partnerExpense || 0 },
      { Chỉ_số: "Lợi nhuận gộp", Giá_trị: report.grossProfit ?? report.profit ?? 0 },
      { Chỉ_số: "Chi phí vận hành", Giá_trị: report.operatingExpense || 0 },
      { Chỉ_số: "Lợi nhuận ròng", Giá_trị: report.netProfit ?? 0 },
      { Chỉ_số: "Biên LN ròng (%)", Giá_trị: report.netMarginPercent ?? 0 },
      { Chỉ_số: "Tổng chi phí", Giá_trị: report.totalExpense || 0 },
    ]);
    XLSX.utils.book_append_sheet(wb, summarySheet, "TomTat");
    const lineSheet = XLSX.utils.json_to_sheet(
      byLineRows.map((r, i) => ({
        STT: i + 1,
        "Line KD": r.lineName || "",
        "Doanh thu": r.revenue || 0,
        "Chi phí NCC": r.partnerExpense || 0,
        "Chi phí vận hành": r.operatingExpense || 0,
        "Tổng chi phí": r.expense || 0,
        "Lợi nhuận gộp": r.grossProfit || 0,
        "Lợi nhuận ròng": r.netProfit || 0,
        "Biên LN ròng (%)": r.netMarginPercent || 0,
      }))
    );
    XLSX.utils.book_append_sheet(wb, lineSheet, "TheoLine");
    XLSX.writeFile(wb, "LoiNhuan.xlsx");
  };

  const periodLabel = getPeriodLabel(appliedFilters.period);
  const rangeHint = appliedFilters.dateFrom && appliedFilters.dateTo
    ? `${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`
    : "";
  const byLine = report?.byLine || [];
  const grossTrend = Array.isArray(report?.grossTrend)
    ? report.grossTrend
    : (Array.isArray(report?.trend) ? report.trend : []);
  const netTrend = Array.isArray(report?.netTrend) ? report.netTrend : [];

  const barData = useMemo(
    () => byLine.map((r) => ({
      name: r.lineName || "—",
      revenue: Number(r.revenue || 0),
      grossProfit: Number(r.grossProfit || 0),
      netProfit: Number(r.netProfit || 0),
    })),
    [byLine]
  );

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title={!embedded ? "Báo cáo Lợi nhuận" : undefined}
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={(
              <Button variant="success" size="sm" onClick={exportExcel} disabled={loading || !report}>
                <FaFileExcel className="me-1" /> Xuất Excel
              </Button>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Thời gian</Form.Label>
                <Form.Select size="sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {period === "CUSTOM" && (
                <>
                  <Col>
                    <Form.Label className="small mb-1">Từ ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label className="small mb-1">Đến ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                  </Col>
                </>
              )}
              <Col>
                <Form.Label className="small mb-1">Line KD</Form.Label>
                <Form.Select size="sm" value={filterLine} onChange={(e) => setFilterLine(e.target.value)}>
                  <option value="">Tất cả</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>{l.name || l.lineName}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters chips={activeChips} onRemove={handleRemoveChip} onClearAll={handleReset} />
        </Card.Body>
      </Card>

      {rangeHint && (
        <div className="text-muted small mb-2">
          Kỳ báo cáo: <strong>{periodLabel}</strong> ({rangeHint})
        </div>
      )}

      <div className="acc-summary-grid--equal">
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard title="Doanh thu" value={report?.totalRevenue} color="#198754" icon={FaMoneyBillWave} loading={loading} compact />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard title="Chi phí NCC" value={report?.partnerExpense} color="#fd7e14" icon={FaHandshake} loading={loading} compact />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="Lợi nhuận gộp"
            value={report?.grossProfit ?? report?.profit}
            color={profitColor(report?.grossProfit ?? report?.profit)}
            icon={FaChartLine}
            loading={loading}
            compact
            onClick={() => setChartMetric("gross")}
            active={chartMetric === "gross"}
          />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard title="Chi phí vận hành" value={report?.operatingExpense} color="#dc3545" icon={FaCogs} loading={loading} compact />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="Lợi nhuận ròng"
            value={report?.netProfit}
            color={profitColor(report?.netProfit)}
            icon={FaBalanceScale}
            loading={loading}
            compact
            onClick={() => setChartMetric("net")}
            active={chartMetric === "net"}
          />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="Biên LN ròng"
            value={report?.netMarginPercent}
            color={profitColor(report?.netMarginPercent)}
            icon={FaPercent}
            loading={loading}
            compact
            format="percent"
            onClick={() => setChartMetric("byLine")}
            active={chartMetric === "byLine"}
          />
        </div>
      </div>

      {loading ? (
        <Card className="mb-3"><Card.Body className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Đang tải…</Card.Body></Card>
      ) : chartMetric === "byLine" ? (
        <Card className="mb-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <Card.Body>
            <h6 className="fw-bold mb-3">Phân tích theo Line KD</h6>
            {barData.length > 0 ? (
              <div className="acc-chart-wrap-lg">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => fmtVND(v)} />
                  <Legend />
                  <Bar dataKey="revenue" name="Doanh thu" fill="#198754" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="grossProfit" name="LN gộp" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="netProfit" name="LN ròng" fill="#6f42c1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center text-muted py-5">Không có dữ liệu</div>
            )}
          </Card.Body>
        </Card>
      ) : (
        <PeriodCompareLineChart
          data={chartMetric === "net" ? netTrend : grossTrend}
          title={
            chartMetric === "net"
              ? `Lợi nhuận ròng — ${periodLabel} vs kỳ trước`
              : `Lợi nhuận gộp — ${periodLabel} vs kỳ trước`
          }
          currentLabel="Kỳ này"
          previousLabel="Kỳ trước"
          currentColor={chartMetric === "net" ? "#6f42c1" : "#0d6efd"}
        />
      )}

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          <div
            className="px-3 pt-3 pb-1"
            role="button"
            tabIndex={0}
            onClick={() => setChartMetric("byLine")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setChartMetric("byLine"); }}
            style={{ cursor: "pointer" }}
          >
            <h6 className="fw-semibold mb-0">Chi tiết theo Line KD</h6>
          </div>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : byLine.length === 0 ? (
            <div className="text-center py-5 text-muted">Không có dữ liệu trong kỳ đã chọn</div>
          ) : (
            <div className="acc-table-wrap">
              <Table striped hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Line KD</th>
                    <th className="text-end">Doanh thu</th>
                    <th className="text-end acc-col-hide-sm">CP NCC</th>
                    <th className="text-end acc-col-hide-sm">CP vận hành</th>
                    <th className="text-end">Tổng CP</th>
                    <th className="text-end">LN gộp</th>
                    <th className="text-end">LN ròng</th>
                    <th className="text-end acc-col-hide-sm">Biên ròng</th>
                  </tr>
                </thead>
                <tbody>
                  {byLine.map((r) => (
                    <tr key={r.lineId ?? r.lineName}>
                      <td>{r.lineName || "—"}</td>
                      <td className="text-end text-success">{fmtVND(r.revenue)}</td>
                      <td className="text-end text-danger acc-col-hide-sm">{fmtVND(r.partnerExpense)}</td>
                      <td className="text-end text-danger acc-col-hide-sm">{fmtVND(r.operatingExpense)}</td>
                      <td className="text-end text-danger">{fmtVND(r.expense)}</td>
                      <td className={`text-end fw-semibold ${Number(r.grossProfit || 0) < 0 ? "text-danger" : "text-primary"}`}>
                        {fmtVND(r.grossProfit)}
                      </td>
                      <td className={`text-end fw-semibold ${Number(r.netProfit || 0) < 0 ? "text-danger" : "text-primary"}`}>
                        {fmtVND(r.netProfit)}
                      </td>
                      <td className="text-end acc-col-hide-sm">
                        {Number(r.netMarginPercent || 0).toLocaleString("vi-VN", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default ProfitList;
